import React, { useEffect, createContext, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import qs from 'qs'
import UtmUtils from '../utils/utmUtils'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { isIOS, isSafari } from 'react-device-detect'
import { useUtm } from '@gojiraf/useutm'

const PIPContext = createContext(null)

export const PIPProvider = ({ children }) => {
  const { postHogTrackEvent } = usePostHogAnalytics()
  /**
   * El elemento a poner en Picture-in-Picture depende del camino de reproducción activo, y
   * los dos montan un `<video>` distinto: TCPlayer lo anida dentro de `#tcplayer-video`, y
   * el camino HLS ES el elemento `#hls-video`. Sólo uno existe a la vez.
   *
   * Devuelve `undefined` si no hay ninguno —licencia bloqueada, o antes de montar— y todos
   * los usos lo tratan: sin esta guarda, salir de PiP en iOS llamaba `.play()` sobre null.
   */
  const getVideoElement = () =>
    document.querySelector('[id="tcplayer-video"] video') ??
    document.getElementById('hls-video') ??
    undefined
  const [isEnabled, setEnabled] = useState(false)
  const [canPIP, setCanPIP] = useState(false)
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium } = UtmUtils.getUtmObject(queryParams)
  const { isAppView } = useUtm(utm_medium)

  const handleEnterPictureInPicture = () => {
    setEnabled(true)
    window.parent.postMessage({ type: 'enterpictureinpicture' }, '*')
  }
  const handleLeavePictureInPicture = () => {
    setEnabled(false)
    window.parent.postMessage({ type: 'leavepictureinpicture' }, '*')
    if (isIOS) getVideoElement()?.play()
  }

  const addEventListeners = () => {
    document.addEventListener('enterpictureinpicture', handleEnterPictureInPicture)
    document.addEventListener('leavepictureinpicture', handleLeavePictureInPicture)
  }

  const removeEventListeners = () => {
    document.removeEventListener('enterpictureinpicture', handleEnterPictureInPicture)
    document.removeEventListener('leavepictureinpicture', handleLeavePictureInPicture)
  }

  const closePIP = async () => {
    try {
      setEnabled(false)
      await document.exitPictureInPicture()
      if (isIOS || isSafari) await getVideoElement()?.webkitSetPresentationMode?.('inline')
    } catch (err) {
      console.log(err)
    }
  }

  const openPIP = async () => {
    try {
      setEnabled(true)
      const video = getVideoElement()
      if (!video) {
        setEnabled(false)
        return
      }
      if (isIOS || isSafari) {
        await video.webkitSetPresentationMode?.('picture-in-picture')
      } else {
        await video.requestPictureInPicture()
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (isEnabled === true) {
      openPIP()
      postHogTrackEvent('pip-open')
    } else {
      closePIP()
      postHogTrackEvent('pip-close')
    }
  }, [isEnabled])

  useEffect(() => {
    addEventListeners()

    return () => {
      removeEventListeners()
    }
  }, [])

  useEffect(() => {
    if (isAppView) {
      setCanPIP(false)
      return
    }
  }, [isAppView])

  const value = {
    isEnabled,
    canPIP,
    setEnabled,
    togglePIP: () => setEnabled(!isEnabled),
    setCanPIP,
    handleLeavePictureInPicture,
  }

  return <PIPContext.Provider value={value}>{children}</PIPContext.Provider>
}

PIPProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const usePIP = () => {
  const context = useContext(PIPContext)
  if (!context) throw new Error('usePIP debe usarse dentro de un PIPProvider')
  return context
}
